// src/modules/app-views/omnichannel/pages/Init/components/ChatMenuBody.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Badge, Avatar, Tag, Tooltip, Spin, Collapse } from 'antd';
import { AudioOutlined, BarChartOutlined, CameraOutlined, ExclamationCircleOutlined, FileOutlined, PlaySquareOutlined, RiseOutlined, UserOutlined, WhatsAppOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { IoHandLeftOutline } from 'react-icons/io5';
import { PiChatSlashBold } from 'react-icons/pi';
import { TbWindow } from 'react-icons/tb';
import { GrGroup } from 'react-icons/gr';
import { VscArchive } from 'react-icons/vsc';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';
import { computeSlaState, computeMetaWindow } from './slaHelpers';
import WhatsAppSourceTag from './WhatsAppSourceTag';

dayjs.extend(relativeTime);

// *******************************************************************************
// COMPONENT
// *******************************************************************************

/**
 * ChatMenuBody — the Collapse with conversation items and all helper render functions.
 */
const ChatMenuBody = ({
  loading,
  myConversations,
  queueConversations,
  othersConversations,
  isOwnerOrAdm,
  activeKeys,
  openKeys,
  onOpenKeysChange,
  currentId,
  hoveredId,
  onHover,
  onOpenChat,
  slaConfig,
  token,
  viewMode,
}) => {
  // Render a single conversation item — used in both groups
  const renderConversationItem = (item, i, list, { isQueue } = {}) => {
    const rawName = item.displayName || item.contactSnapshot?.name || '—';
    const name = /^\d+$/.test(rawName) ? formatNumericName(rawName) : rawName;
    const lastMsg = getLastMessagePreview(item.messages?.[item.messages.length - 1], item.isGroup);
    const isSelected = item._id === currentId;
    const isLast = i === list.length - 1;
    const isHovered = hoveredId === item._id;
    const hasUnread = item.unreadCount > 0;
    const avatarSrc = item.contactSnapshot?.avatar;
    // Last closed/escalated context — for closed view line 3
    const lastContext = viewMode === 'closed' ? getLastClosedContext(item) : null;
    // Initials fallback: first letter of name, or first digit of phone
    // If the first char is not a letter/digit, use UserOutlined icon instead
    const rawInitial = (name !== '—' ? name : item.contactSnapshot?.phone || '?').charAt(0).toUpperCase();
    const isValidInitial = /[A-Za-z0-9]/.test(rawInitial);
    const avatarInitial = isValidInitial ? rawInitial : null;

    return (
      <div
        key={`chat-item-${item._id}`}
        onClick={() => onOpenChat(item._id)}
        onMouseEnter={() => onHover(item._id)}
        onMouseLeave={() => onHover(null)}
        className={`chat-menu-list-item ${isLast ? 'last' : ''} ${isSelected ? 'selected' : ''}`}
        style={{
          background: isSelected
            ? token.colorPrimaryBg
            : isHovered
              ? token.colorFillSecondary
              : 'transparent',
          borderBottomColor: token.colorBorderSecondary,
          alignItems: 'stretch',
        }}
      >
        {/* Avatar + optional GROUP label */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 10,
          }}
        >
          <Avatar
            src={avatarSrc || undefined}
            shape={item.isGroup ? 'square' : 'circle'}
            style={{ border: '1px solid #d9d9d9', opacity: item.archived ? 0.3 : 1 }}
            icon={item.isGroup && !avatarSrc ? <GrGroup /> : (!avatarInitial && !avatarSrc ? <UserOutlined /> : undefined)}
          >
            {item.isGroup ? null : avatarInitial}
          </Avatar>
          {/*{(item.isGroup || item.archived) && (*/}
          {/*  <span*/}
          {/*    className="text-muted"*/}
          {/*    style={{*/}
          {/*      fontSize: 9,*/}
          {/*      lineHeight: 1,*/}
          {/*      marginTop: 2,*/}
          {/*      textTransform: 'uppercase',*/}
          {/*      letterSpacing: 0.5,*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    {item.archived ? translateX('archived') : translateX('group')}*/}
          {/*  </span>*/}
          {/*)}*/}
        </div>

        {/* Center: name (top) + last message (bottom) */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // gap: 2,
          }}
        >
          {/* Line 1: Name + icons + Time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              className="avatar-status-name"
              style={{
                color: token.colorText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                opacity: item.archived ? 0.3 : 1,
              }}
            >
              {name}
            </span>
            {item.isGroup && (
              <Tooltip title={translateX('group')}>
                <GrGroup style={{ fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
            {item.archived && (
              <Tooltip title={translateX('archived')}>
                <VscArchive style={{ fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
            {/* Closed/Escalated icon — only in closed view */}
            {viewMode === 'closed' &&
              lastContext &&
              (lastContext.status === 'escalated' ? (
                <Tooltip title={translateX('escalated')}>
                  <RiseOutlined style={{ fontSize: 12, flexShrink: 0, color: '#faad14' }} />
                </Tooltip>
              ) : (
                <Tooltip title={translateX('closed')}>
                  <PiChatSlashBold
                    style={{ fontSize: 12, flexShrink: 0, display: 'flex', color: '#ff4d4f' }}
                  />
                </Tooltip>
              ))}
            {item.sourceType && (
              <WhatsAppSourceTag sourceType={item.sourceType} size="small" />
            )}
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                color: hasUnread ? '#3e82f7' : token.colorTextSecondary,
              }}
            >
              {viewMode === 'closed' && lastContext?.closedAt
                ? dayjs(lastContext.closedAt).locale('pt-br').fromNow()
                : formatLastMessageTime(item.lastMessageAt)
              }
            </span>
          </div>

          {/* Line 2: Last message + Unread badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              className="text-muted avatar-status-subtitle"
              style={{
                flex: 1,
                minWidth: 0,
                color: token.colorTextSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                opacity: item.archived ? 0.3 : 1,
                ...(isQueue ? { filter: 'blur(5px)', userSelect: 'none' } : {}),
              }}
            >
              {lastMsg}
            </span>
            {hasUnread && (
              <Badge
                count={item.unreadCount}
                style={{ backgroundColor: '#3e82f7', flexShrink: 0 }}
              />
            )}
          </div>

          {/* Line 3 (active): SLA + Meta window */}
          {viewMode !== 'closed' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 4,
                marginTop: 4,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>{renderSlaLine(item, slaConfig)}</div>
              {renderMetaWindowBadge(item)}
            </div>
          )}

          {/* Line 3 (closed): Description + Reason */}
          {viewMode === 'closed' && lastContext && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 4,
                marginTop: 4,
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                }}
              >
                "{lastContext.description || '—'}"
              </span>
              {lastContext.reason && (
                <Tag
                  color={getReasonColor(lastContext.reason)}
                  style={{
                    fontSize: 10,
                    lineHeight: '16px',
                    padding: '0 4px',
                    flexShrink: 0,
                    margin: 0,
                  }}
                >
                  {translateX(`close_reason_${lastContext.reason}`)}
                </Tag>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Closed view — flat list without Collapse groups
  if (viewMode === 'closed') {
    return (
      <div className="chat-menu-list" style={{ overflowX: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : myConversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: token.colorTextSecondary, fontSize: 12 }}>
            {translateX('no_conversations')}
          </div>
        ) : (
          <div style={{ marginBottom: 88 }}>
            {myConversations.map((item, i) =>
              renderConversationItem(item, i, myConversations)
            )}
          </div>
        )}
      </div>
    );
  }

  // Active view — Collapse with "My Conversations" and "Queue"
  return (
    <div className="chat-menu-list" style={{ overflowX: 'hidden' }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <Collapse
          activeKey={openKeys ?? activeKeys}
          onChange={onOpenKeysChange}
          bordered={false}
          ghost
          style={{ background: 'transparent', borderRadius: 0 }}
          styles={{
            header: { padding: '8px 16px', borderRadius: 0 },
            body: { padding: 0 },
            item: { borderRadius: 0 },
          }}
          items={[
            {
              key: 'mine',
              label: (
                <Badge.Ribbon
                  color="cyan"
                  text={
                    myConversations.length ? (
                      <span style={{ paddingRight: 12 }}>{myConversations.length}</span>
                    ) : null
                  }
                >
                  <span
                    className="text-muted"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: 'uppercase',
                      display: 'inline-flex',
                      alignItems: 'start',
                    }}
                  >
                    {translateX('my_conversations')}
                    {/*{myConversations.length > 0 && (*/}
                    {/*  <Badge*/}
                    {/*    count={myConversations.length}*/}
                    {/*    style={{ backgroundColor: '#c0c0c0', marginLeft: 8 }}*/}
                    {/*    // size="small"*/}
                    {/*  />*/}
                    {/*)}*/}
                  </span>
                </Badge.Ribbon>
              ),
              children:
                myConversations.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: 12,
                      color: token.colorTextSecondary,
                      fontSize: 12,
                    }}
                  >
                    {translateX('no_conversations')}
                  </div>
                ) : (
                  myConversations.map((item, i) =>
                    renderConversationItem(item, i, myConversations)
                  )
                ),
            },
            {
              key: 'queue',
              label: (
                <Badge.Ribbon
                  color="volcano"
                  text={
                    queueConversations.length ? (
                      <span style={{ paddingRight: 12 }}>{queueConversations.length}</span>
                    ) : null
                  }
                >
                  <span
                    className="text-muted"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: 'uppercase',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {translateX('service_queue')}
                    {/*{queueConversations.length > 0 && (*/}
                    {/*  <Badge*/}
                    {/*    count={queueConversations.length}*/}
                    {/*    style={{ backgroundColor: '#faad14', marginLeft: 8 }}*/}
                    {/*    // size="small"*/}
                    {/*  />*/}
                    {/*)}*/}
                  </span>
                </Badge.Ribbon>
              ),
              children:
                queueConversations.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: 12,
                      color: token.colorTextSecondary,
                      fontSize: 12,
                    }}
                  >
                    {translateX('no_conversations')}
                  </div>
                ) : (
                  queueConversations.map((item, i) =>
                    renderConversationItem(item, i, queueConversations, { isQueue: true })
                  )
                ),
            },
            ...(isOwnerOrAdm ? [
              {
                key: 'others',
                style: { marginBottom: 88 },
                label: (
                  <Badge.Ribbon
                    color="blue"
                    text={
                      othersConversations.length ? (
                        <span style={{ paddingRight: 12 }}>{othersConversations.length}</span>
                      ) : null
                    }
                  >
                    <span
                      className="text-muted"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        textTransform: 'uppercase',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {translateX('others_conversations')}
                    </span>
                  </Badge.Ribbon>
                ),
                children:
                  othersConversations.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: 12,
                        color: token.colorTextSecondary,
                        fontSize: 12,
                      }}
                    >
                      {translateX('no_conversations')}
                    </div>
                  ) : (
                    othersConversations.map((item, i) =>
                      renderConversationItem(item, i, othersConversations, { isReadOnly: true })
                    )
                  ),
              },
            ] : []),
          ]}
        />
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChatMenuBody;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Returns the text (or JSX) to display as the last message preview in the conversation list.
 * - Media messages (audio, image, video, document): icon + type label
 * - Outbound (agent): always shows what the agent typed (originalText > text)
 * - Inbound (client): shows cached translation if a reading lang is active, otherwise original
 */
function getLastMessagePreview(msg, isGroup) {
  if (!msg?.content) return '';

  // Sender first name prefix — only for group conversations (redundant for 1:1)
  const senderPrefix = isGroup ? getSenderPrefix(msg) : null;

  // Media type previews — replicate WhatsApp style with icon + label
  const mediaType = msg.content.mediaType;
  if (mediaType) {
    const mediaPreview = getMediaPreview(mediaType, msg.content);
    if (mediaPreview) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {senderPrefix}{mediaPreview}
        </span>
      );
    }
  }

  let text;
  if (msg.direction === 'outbound') {
    // Show what the agent typed, not what was delivered (avoid showing translated delivery)
    text = msg.content.originalText || msg.content.text || '';
  } else {
    // Inbound: prefer translation matching the active reading lang
    const readingLang = localStorage.getItem('omnichannel_translate_lang');
    if (readingLang && readingLang !== 'none' && Array.isArray(msg.translations)) {
      const cached = msg.translations.find(t => t.lang === readingLang);
      if (cached?.text) {
        text = cached.text;
      }
    }
    text = text || msg.content.text || '';
  }

  // Unsupported message type — show friendly label instead of raw marker
  if (text && isUnsupportedMessage(text)) {
    const unsupported = (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <ExclamationCircleOutlined style={{ fontSize: 13, marginRight: 4, color: '#faad14' }} />
        {translateX('unsupported_message')}
      </span>
    );
    if (!senderPrefix) return unsupported;
    return <span>{senderPrefix}{unsupported}</span>;
  }

  if (!senderPrefix) return text;
  return <span>{senderPrefix}{text}</span>;
}

function getSenderPrefix(msg) {
  const name = msg.sender?.name;
  if (!name) return null;
  // Use first name only to keep it compact
  const firstName = name.split(' ')[0];
  return <span style={{ fontWeight: 600 }}>{firstName}: </span>;
}

function getMediaPreview(mediaType, content) {
  const iconStyle = { fontSize: 13, marginRight: 4, color: '#25D366' };
  const caption = content.caption || content.text;

  switch (mediaType) {
    case 'audio': {
      const dur = content.duration > 0 ? ` (${formatAudioDuration(content.duration)})` : '';
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <AudioOutlined style={iconStyle} />
          {translateX('voice_message')}{dur}
        </span>
      );
    }
    case 'image':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <CameraOutlined style={iconStyle} />
          {caption || translateX('photo')}
        </span>
      );
    case 'video':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <PlaySquareOutlined style={iconStyle} />
          {caption || translateX('video')}
        </span>
      );
    case 'document':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <FileOutlined style={iconStyle} />
          {content.fileName || caption || translateX('document')}
        </span>
      );
    case 'sticker':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span style={{ marginRight: 4 }}>🎃</span>
          Sticker
        </span>
      );
    case 'poll':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <BarChartOutlined style={iconStyle} />
          {content.text || translateX('poll')}
        </span>
      );
    default:
      return null;
  }
}

/**
 * Format the last message timestamp following WhatsApp Web rules:
 * - Today      → HH:MM  (e.g. "14:32")
 * - This week  → weekday name  (e.g. "quinta-feira")
 * - Older      → DD/MM/YYYY  (e.g. "04/03/2026")
 */
function formatLastMessageTime(timestamp) {
  if (!timestamp) return '';

  const msgDate = new Date(timestamp);
  const now = new Date();

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(msgDate, now)) {
    return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const msgStart = new Date(msgDate);
  msgStart.setHours(0, 0, 0, 0);
  const diffDays = Math.round((todayStart - msgStart) / (1000 * 60 * 60 * 24));

  if (diffDays <= 6) {
    return msgDate.toLocaleDateString([], { weekday: 'long' });
  }

  return msgDate.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Returns the Collapse activeKeys based on which group contains the selected conversation.
 * No currentId → all collapsed. With currentId → open the group where it belongs.
 */
function getActiveKeys(currentId, myConversations, queueConversations) {
  if (!currentId) return [];

  if (myConversations.some(c => c._id === currentId)) return ['mine'];
  if (queueConversations.some(c => c._id === currentId)) return ['queue'];

  // Conversation not found in either group (maybe filtered out by search) → all collapsed
  return [];
}

// Render the SLA third line for a conversation item
function renderSlaLine(item, slaConfig) {
  const sla = computeSlaState(item, slaConfig);
  if (!sla) return null;

  const icon =
    sla.icon === 'pause' ? (
      <IoHandLeftOutline style={{ fontSize: 11, flexShrink: 0, lineHeight: 1, display: 'flex' }} />
    ) : (
      <ClockCircleOutlined style={{ fontSize: 11, flexShrink: 0, lineHeight: 1 }} />
    );

  return (
    <Tooltip title={sla.tooltip} placement="bottomLeft">
      <div
        style={{
          fontSize: 11,
          lineHeight: 1,
          color: sla.color,
          fontWeight: sla.mode !== 'waiting' ? 600 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          letterSpacing: 0.2,
        }}
      >
        {icon}
        <span>{sla.label}</span>
      </div>
    </Tooltip>
  );
}

// Render the Meta 24h window badge (right side of third line)
function renderMetaWindowBadge(item) {
  // Unofficial (baileys) — always open, no window restriction
  if (item.sourceType === 'baileys') {
    return (
      <Tooltip title={translateX('window_open')} placement="bottomRight">
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            fontSize: 11,
            lineHeight: 1,
            color: '#52c41a',
            flexShrink: 0,
          }}
        >
          <TbWindow style={{ fontSize: 12, display: 'flex' }} />
          <span className='text-uppercase'>{translateX('open_f')}</span>
        </div>
      </Tooltip>
    );
  }

  // Meta — compute 24h window
  if (item.sourceType === 'meta') {
    const win = computeMetaWindow(item);
    // const color = win.open ? '#52c41a' : '#ff4d4f';
    const color = win.open ? '#ff9800' : '#ff4d4f';
    const label = win.open ? win.label : translateX('closed_f');
    return (
      <Tooltip title={translateX(win.tooltip)} placement="bottomRight">
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            fontSize: 11,
            lineHeight: 1,
            color,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          <TbWindow style={{ fontSize: 12, display: 'flex' }} />
          <span className='text-uppercase'>{label}</span>
        </div>
      </Tooltip>
    );
  }

  return null;
}

// Format audio duration in seconds to M:SS (e.g. 65 → "1:05", 5 → "0:05")
function formatAudioDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Map closure reason to AntD v6 preset Tag color (outlined)
function getReasonColor(reason) {
  switch (reason) {
    case 'resolved': return 'green';
    case 'doubt': return 'purple';
    case 'mistake': return 'magenta';
    case 'no_response': return 'gold';
    case 'other': return 'geekblue';
    default: return 'default';
  }
}

// Detect backend-generated unsupported message markers
// Format a numeric name (phone number) as masked display
function formatNumericName(num) {
  if (!num) return '—';
  const digits = num.replace(/\D/g, '');
  if (digits.length <= 11) {
    return parsePhoneToDisplay(num, 'BR', { showCountryCode: true, masked: true });
  }
  const withPlus = digits.startsWith('+') ? digits : `+${digits}`;
  return parsePhoneToDisplay(withPlus, undefined, { showCountryCode: true, masked: true });
}

function isUnsupportedMessage(text) {
  if (!text) return false;
  const t = text.trim().toLowerCase();
  return t.startsWith('[unsupported') || t.startsWith('[tipo de mensagem');
}

// Get the last closed or escalated context from a conversation
function getLastClosedContext(item) {
  const contexts = item.contexts || [];
  for (let i = contexts.length - 1; i >= 0; i--) {
    if (contexts[i].status === 'closed' || contexts[i].status === 'escalated') {
      return contexts[i];
    }
  }
  return null;
}
